'use client';

import { useEffect, useRef } from 'react';
import { useQuality } from '@/components/shared/QualityProvider';
import { setMountProgress } from '@/lib/journeyState';
import { LOGO_MOUNT_PHASE_SPLIT } from '@/lib/motionTokens';

const FRAGMENTS_BASE = '/logos/logo-fragments';

// Container geometry mirrors `IntroLogoBreak` so the assembled W is
// pixel-identical to the brand mark loaded at the very top of the page.
// LAYOUT / EXITS are intentional duplicates rather than shared imports:
// the break component renders inside the journey scaffold (desktop only,
// dynamic-imported) and we don't want to drag those imports into the
// shared scroll-driven runtime.
const CONTAINER_W = 159;
const CONTAINER_H = 100;

const LAYOUT = {
  darkL:  { top: 4,  left: 14, height: 93, src: 'Vector-1.png',  restZ: -50 },
  darkR:  { top: 4,  left: 49, height: 93, src: 'Vector.png',    restZ: -50 },
  innerV: { top: 4,  left: 36, height: 36, src: 'Group 240.png', restZ: -10 },
  lightV: { top: 4,  left: 29, height: 92, src: 'Vector-3.png',  restZ:  28 },
  dot:    { top: 57, left: 1,  height: 39, src: 'Vector-2.png',  restZ:  50 },
} as const;

type FragmentKey = keyof typeof LAYOUT;
// Keyed off the rendered fragments only — 'dot' is intentionally absent
// because the central blob from TrailingBlobCanvas takes its place at
// the end of the morph (see `computeBlobDotTarget` there). Narrowing
// the union here lets the per-fragment animation map below stay typed
// without an `Exclude<…, 'dot'>` at every callsite.
type RenderedKey = Exclude<FragmentKey, 'dot'>;
const KEYS: readonly RenderedKey[] = ['darkL', 'darkR', 'innerV', 'lightV'];

// Trigger: once the blob's `positionFloat` crosses this value, the morph
// starts climbing on a TIME-based curve, independent of further scroll.
// This decouples the animation completion from chapter 4's height — the
// hijack snaps the user into chapter 4 in ~1.4s and the morph keeps
// building for another MORPH_DURATION_SEC seconds while they read the
// CTA. Section height stays unchanged.
const TRIGGER_POSITION = 3.3;

// Forward duration: how long the morph takes to reach 1 once triggered.
// Tuned to outlast the hijack snap so the W resolves during the user's
// dwell at chapter 4, not during the snap itself.
const MORPH_DURATION_SEC = 2.5;

// Reverse duration: when the user scrolls BACK above the trigger, the
// progress decays at this rate. Snappier than the forward direction so
// the blob/W bounce back without lingering — undo feels decisive.
const MORPH_REVERSE_DURATION_SEC = 0.6;

// Subtle stage scale settle during the W phase. Most of the visual
// interest comes from the per-fragment cascade below; the stage scale is
// here just to give the assembled mark a small "click into final size"
// micro-moment at the very end.
const STAGE_W_PHASE_START_SCALE = 0.96;

const TWO_PI = Math.PI * 2;

// Per-fragment idle drift, modelled on IntroLogoBreak's IDLE table —
// each piece has its own Lissajous orbit (independent periods + phase
// offsets across 6 axes) so the assembled W is never a static frame.
// The amplitudes here are about half of the break's because the
// fragments are still LOCKED into the W silhouette (not floating debris
// like during the explosion), so smaller motion sells "life" instead of
// "drift".
//
// Periods (`px`, `py`) are in SECONDS — sine wraps every `px` seconds.
// Phase offsets (`phx`, `phy`) are radians and keep the four fragments
// from syncing into an obvious group rock.
//
// idleRamp inside the tick gates this so a fragment's drift only kicks
// in once its OWN cascade is done — early fragments start dancing while
// later ones are still arriving, giving the assembly an overlapping
// "wake up sequentially" feel.
type IdleSpec = {
  tx: number; ty: number; tz: number;
  rx: number; ry: number; rz: number;
  px: number; py: number;
  phx: number; phy: number;
};

const FRAGMENT_IDLE: Record<RenderedKey, IdleSpec> = {
  darkL:  { tx: 0.5,  ty: 0.6, tz: 7,  rx: 0.7, ry: 1.3, rz: 0.4, px: 8.3, py: 6.7, phx: 1.5, phy: 0.3 },
  darkR:  { tx: 0.55, ty: 0.7, tz: 6.5, rx: 0.8, ry: 1.4, rz: 0.5, px: 7.9, py: 7.1, phx: 3.7, phy: 5.5 },
  innerV: { tx: 1.8,  ty: 2.0, tz: 12, rx: 2.8, ry: 3.3, rz: 2.3, px: 4.7, py: 5.3, phx: 0.8, phy: 2.7 },
  lightV: { tx: 0.7,  ty: 1.1, tz: 12, rx: 1.2, ry: 0.7, rz: 0.8, px: 6.5, py: 5.1, phx: 2.3, phy: 4.1 },
};

// Per-fragment idle starts ramping in once its own cascade is mostly
// done (fragLocal > IDLE_FRAG_RAMP_START) and reaches full strength at
// fragLocal=1. Smoothstep on the ramp window means there's no hard
// hand-off between cascade decay and idle climb.
const IDLE_FRAG_RAMP_START = 0.85;

// Per-fragment cascade choreography. Each fragment has its own (delay,
// duration, from-transform) tuple within the W phase (mountProgress
// LOGO_MOUNT_PHASE_SPLIT → 1). The from-transform is the offset the
// fragment carries at fragLocal=0; it decays to zero as fragLocal goes
// to 1, leaving the fragment at its rest LAYOUT position.
//
// Choices behind the numbers:
//   • Reading order darkL → lightV → innerV → darkR: assembles left to
//     right around the blob (which sits in the dot slot, lower-left).
//   • Heavy negative tz on darkL/darkR + ±rotateY: the two thick sides
//     of the W swing in from depth like saloon doors — gives the W a
//     "folding into place" feel beyond just opacity.
//   • lightV and innerV come from straight below with rotateX — they
//     read as panels tipping forward, not flat fades.
//   • The cascade closes on darkR ending at wPhase 0.95, giving a final
//     rest beat before the W is fully settled.
type FragmentAnim = {
  delay: number;
  duration: number;
  from: { tx: number; ty: number; tz: number; rx: number; ry: number; rz: number };
};

const FRAGMENT_ANIMATIONS: Record<RenderedKey, FragmentAnim> = {
  darkL:  { delay: 0.00, duration: 0.55, from: { tx: -14, ty: 14, tz: -150, rx: -22, ry:  28, rz: -8 } },
  lightV: { delay: 0.18, duration: 0.55, from: { tx:   0, ty: 22, tz: -110, rx: -28, ry:   0, rz:  0 } },
  innerV: { delay: 0.28, duration: 0.55, from: { tx:   0, ty: 14, tz:  -70, rx: -16, ry:   0, rz:  0 } },
  darkR:  { delay: 0.40, duration: 0.55, from: { tx:  14, ty: 14, tz: -150, rx: -22, ry: -28, rz:  8 } },
};

function clamp01(t: number): number {
  return Math.max(0, Math.min(1, t));
}

// Perlin's 5th-order smoothstep — flatter shoulders than the cubic
// smoothstep, so the curve lingers longer near 0 and 1. That extra dwell
// at both ends is what reads as "slow and sensual" instead of "fast
// reveal" — the W creeps in at start, settles at end, and only the
// middle has appreciable motion. Used for stage scale, fragment opacity,
// AND the blob's position/scale lerp inside TrailingBlobCanvas (both
// components import their own copy to keep the dependency graph one-way).
function smootherstep(t: number): number {
  const x = clamp01(t);
  return x * x * x * (x * (x * 6 - 15) + 10);
}

/**
 * Reverse of `IntroLogoBreak`. Anchored to the InfoSection's last
 * chapter (`.chapter-station--04`, the contact close), this component
 * scrolls a 5-fragment W onto the screen from the same explosion
 * coordinates the intro break used to throw them away. Pairs with
 * `TrailingBlobCanvas`, which reads `mountProgress` from the shared
 * external store and fades the 3D blob in lock-step — so the visual
 * reads as the blob morphing INTO the W instead of two separate
 * elements crossfading.
 *
 * Skipped on `low` tier (no blob to morph from) and under
 * prefers-reduced-motion. The fixed overlay lives at z-20, behind the
 * header (z-50) and skip link (z-10000) but above the trailing blob's
 * canvas, so the assembled W reads on top of the blob silhouette
 * during the cross-fade window.
 */
export function IntroLogoMount() {
  const { tier } = useQuality();
  const stageRef = useRef<HTMLDivElement>(null);
  const refs = useRef<Record<RenderedKey, HTMLImageElement | null>>({
    darkL: null,
    lightV: null,
    innerV: null,
    darkR: null,
  });

  const enabled = tier === 'high' || tier === 'mid';

  useEffect(() => {
    if (!enabled) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;

    let rafId = 0;
    let infoSection: HTMLElement | null = null;
    // Time-based tween state. Held in closures (not refs) because this
    // RAF loop is the only writer; React doesn't need to see the value.
    let progress = 0;
    let lastTickMs = 0;

    function tick() {
      // Lazy DOM lookup — InfoSection mounts after first paint, so the
      // wrapper may not exist on tick #0.
      if (!infoSection) {
        infoSection = document.querySelector<HTMLElement>('.info-section');
      }
      if (!infoSection) {
        rafId = requestAnimationFrame(tick);
        return;
      }

      const nowMs = performance.now();
      // Clamp dt at 0.1s — guards against huge frame gaps after tab
      // backgrounding so the tween doesn't jump straight to 1 on resume.
      const dtSec = lastTickMs === 0 ? 0 : Math.min(0.1, (nowMs - lastTickMs) / 1000);
      lastTickMs = nowMs;

      const infoRect = infoSection.getBoundingClientRect();
      const vh = window.innerHeight;

      // Same positionFloat formula `TrailingBlobCanvas` uses for the
      // blob's chapter-to-chapter trajectory. Used only to read the
      // trigger threshold — NOT as a scrub.
      const positionFloat = Math.max(0, Math.min(4, 1 + -infoRect.top / vh));

      // Time-driven tween toward 0/1 based on which side of the trigger
      // the user is on. Asymmetric speeds: forward is slow & sensual,
      // reverse is decisive. The trick that makes this feel right —
      // mountProgress can climb past where any scroll position would
      // place it, because the section height isn't dictating the
      // animation's length.
      const target = positionFloat >= TRIGGER_POSITION ? 1 : 0;

      // Idle fast-path: when the user is far from chapter 4 and the
      // tween has long since settled at 0 (target === 0, progress === 0),
      // there's nothing to compute and nothing visible to update. Skip
      // the 24 per-fragment sin() calls + opacity/transform writes that
      // would otherwise burn ~1.5kHz of main-thread work for no payoff.
      if (target === 0 && progress === 0) {
        rafId = requestAnimationFrame(tick);
        return;
      }

      const climbing = target > progress;
      const speed =
        climbing ? 1 / MORPH_DURATION_SEC : 1 / MORPH_REVERSE_DURATION_SEC;
      const delta = target - progress;
      const step = Math.sign(delta) * Math.min(Math.abs(delta), speed * dtSec);
      progress = clamp01(progress + step);

      setMountProgress(progress);

      // W phase: the fragments only start appearing AFTER the blob has
      // reached the dot slot (mountProgress ≥ LOGO_MOUNT_PHASE_SPLIT).
      // Before that the stage is dark — no daylight between blob motion
      // and W assembly.
      const wPhaseRaw = (progress - LOGO_MOUNT_PHASE_SPLIT) / (1 - LOGO_MOUNT_PHASE_SPLIT);
      const wPhase = clamp01(wPhaseRaw);

      const infoOnScreen = infoRect.bottom > 0;
      const visible = infoOnScreen && wPhase > 0;

      const stage = stageRef.current;
      if (stage) {
        stage.style.opacity = visible ? '1' : '0';
        // Stage is back to a single scale settle — the per-fragment
        // idle below carries all the motion now. Wrapping the whole W
        // in a stage-level drift on top of that would double the
        // amplitude and read as "the whole logo is wobbling" rather
        // than "each piece is alive on its own".
        const stageScale =
          STAGE_W_PHASE_START_SCALE + (1 - STAGE_W_PHASE_START_SCALE) * smootherstep(wPhase);
        stage.style.transform = `scale(${stageScale})`;
      }

      const tSec = nowMs / 1000;

      // Per-fragment cascade + per-fragment idle drift. The cascade
      // FROM transform decays to zero over the fragment's local window;
      // the idle motion ramps up over the END of that same window and
      // continues forever after. The two are additive, so a fragment
      // mid-cascade also already shows a hint of idle motion bleeding
      // in — the wake-up is seamless.
      for (const key of KEYS) {
        const el = refs.current[key];
        if (!el) continue;
        const anim = FRAGMENT_ANIMATIONS[key];
        const idle = FRAGMENT_IDLE[key];
        const restZ = LAYOUT[key].restZ;

        const fragLocalRaw = (wPhase - anim.delay) / anim.duration;
        const fragLocal = clamp01(fragLocalRaw);
        const settled = smootherstep(fragLocal);
        const drift = 1 - settled;

        // Idle drift — six independent sinusoids per fragment so the
        // motion never repeats exactly. Ramps in over the back end of
        // the cascade window so the fragment "wakes up" as it locks
        // into place, not after a beat of stillness.
        const idleRamp = smootherstep(
          clamp01((fragLocal - IDLE_FRAG_RAMP_START) / (1 - IDLE_FRAG_RAMP_START)),
        );
        const sx = Math.sin((tSec * TWO_PI) / idle.px + idle.phx);
        const sy = Math.sin((tSec * TWO_PI) / idle.py + idle.phy);
        const sz = Math.sin((tSec * TWO_PI) / (idle.px * 1.3) + idle.phx + 0.7);
        const srx = Math.sin((tSec * TWO_PI) / (idle.py * 1.1) + idle.phy + 1.4);
        const sry = Math.sin((tSec * TWO_PI) / (idle.px * 1.5) + idle.phx + 2.1);
        const srz = Math.sin((tSec * TWO_PI) / idle.py + idle.phy + 0.5);

        const tx = anim.from.tx * drift + idle.tx * sx * idleRamp;
        const ty = anim.from.ty * drift + idle.ty * sy * idleRamp;
        const tz = restZ + anim.from.tz * drift + idle.tz * sz * idleRamp;
        const rx = anim.from.rx * drift + idle.rx * srx * idleRamp;
        const ry = anim.from.ry * drift + idle.ry * sry * idleRamp;
        const rz = anim.from.rz * drift + idle.rz * srz * idleRamp;

        el.style.transform =
          `translate3d(${tx}%, ${ty}%, ${tz}px)` +
          ` rotateX(${rx}deg)` +
          ` rotateY(${ry}deg)` +
          ` rotateZ(${rz}deg)`;
        el.style.opacity = String(settled);
      }

      rafId = requestAnimationFrame(tick);
    }

    rafId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafId);
      // Release the signal so a route change / fast refresh doesn't
      // leave the trailing blob faded out on the next page.
      setMountProgress(0);
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 z-20 flex items-center justify-center overflow-hidden"
      style={{ perspective: '1400px', perspectiveOrigin: '50% 45%' }}
    >
      <div
        ref={stageRef}
        className="relative h-[24vh] max-h-[200px] md:h-[34vh] md:max-h-[300px]"
        style={{
          aspectRatio: `${CONTAINER_W} / ${CONTAINER_H}`,
          transformStyle: 'preserve-3d',
          willChange: 'transform, opacity',
          opacity: 0,
        }}
      >
        {KEYS.map((key) => {
          const cfg = LAYOUT[key];
          return (
            <img
              key={key}
              ref={(el) => {
                refs.current[key] = el;
              }}
              src={`${FRAGMENTS_BASE}/${cfg.src}`}
              alt=""
              aria-hidden
              draggable={false}
              className="absolute w-auto select-none"
              style={{
                top: `${cfg.top}%`,
                left: `${cfg.left}%`,
                height: `${cfg.height}%`,
                backfaceVisibility: 'hidden',
                willChange: 'transform, opacity',
                opacity: 0,
              }}
            />
          );
        })}
      </div>
    </div>
  );
}
